import { useForm } from "@inertiajs/react"
import { useEffect } from "react"

import { PRICING_FORMAT_LABELS } from "@/components/bids/BidStatusBadge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type { WorkspaceBid, WorkspaceCommunity } from "@/types/bids"

/** The bid's header knobs (use-tax %, validity window, prepared-for line —
 *  use tax is a COGS input: margins move, quoted prices never do) plus the
 *  BUILDER's two document knobs, which apply to every bid for the builder:
 *  the default sheet format (each revision can override at generate time)
 *  and net-vs-waste-in quantities. */
export function BidSettingsDialog({
  open,
  onOpenChange,
  bid,
  customer,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  bid: WorkspaceBid
  customer: WorkspaceCommunity["customer"]
}) {
  const form = useForm({
    bid: {
      use_tax_pct: bid.use_tax_pct,
      valid_days: bid.valid_days,
      prepared_for: bid.prepared_for ?? "",
    },
  })
  const builderForm = useForm({
    customer: {
      pricing_format: customer.pricing_format,
      waste_handling: customer.waste_handling,
    },
  })

  useEffect(() => {
    if (!open) return
    form.setData("bid", {
      use_tax_pct: bid.use_tax_pct,
      valid_days: bid.valid_days,
      prepared_for: bid.prepared_for ?? "",
    })
    form.clearErrors()
    builderForm.setData("customer", {
      pricing_format: customer.pricing_format,
      waste_handling: customer.waste_handling,
    })
    builderForm.clearErrors()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed on open only
  }, [open, bid, customer])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    form.patch(bid.urls.update, { preserveScroll: true, onSuccess: () => onOpenChange(false) })
  }

  const submitBuilder = (e: React.FormEvent) => {
    e.preventDefault()
    builderForm.patch(customer.update_url, {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" data-testid="bid-settings-dialog">
        <DialogHeader>
          <DialogTitle>Bid settings</DialogTitle>
          <DialogDescription>
            {bid.bid_number} — the use-tax rate feeds every COGS; the rest prints on the bid sheet.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="bid-use-tax">Use tax</label>
            <div className="flex items-center gap-2">
              <Input
                id="bid-use-tax"
                type="number"
                step="any"
                className="w-28"
                value={form.data.bid.use_tax_pct}
                onChange={(e) => form.setData("bid", { ...form.data.bid, use_tax_pct: e.target.value })}
                data-testid="bid-use-tax"
              />
              <span className="text-sm text-ink-muted">% on material</span>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="bid-valid-days">Valid for</label>
            <div className="flex items-center gap-2">
              <Input
                id="bid-valid-days"
                type="number"
                className="w-28"
                value={form.data.bid.valid_days}
                onChange={(e) =>
                  form.setData("bid", { ...form.data.bid, valid_days: Number(e.target.value) })
                }
              />
              <span className="text-sm text-ink-muted">days</span>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="bid-prepared-for">Prepared for</label>
            <Input
              id="bid-prepared-for"
              value={form.data.bid.prepared_for}
              onChange={(e) => form.setData("bid", { ...form.data.bid, prepared_for: e.target.value })}
              placeholder="DRB Group — Charlotte Division"
            />
          </div>
          <div className="modal-footer">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={form.processing}>
              Save settings
            </Button>
          </div>
        </form>

        <form onSubmit={submitBuilder} className="mt-2 space-y-4 border-t border-hairline pt-4">
          <div>
            <p className="font-medium text-ink-display">Builder — {customer.name}</p>
            <p className="text-sm text-ink-muted">Applies to every bid for this builder.</p>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="builder-pricing-format">Default sheet format</label>
            <Select
              id="builder-pricing-format"
              value={builderForm.data.customer.pricing_format}
              onChange={(e) =>
                builderForm.setData("customer", {
                  ...builderForm.data.customer,
                  pricing_format: e.target.value as typeof customer.pricing_format,
                })
              }
              data-testid="builder-pricing-format"
            >
              <option value="room_level_totals">
                {PRICING_FORMAT_LABELS.room_level_totals} — room-by-room quote per plan
              </option>
              <option value="sf_price_only">
                {PRICING_FORMAT_LABELS.sf_price_only} — one-page rate card
              </option>
            </Select>
            <span className="text-sm text-ink-muted">
              The sheet page can override per revision — rate card first, full pricing after
              winning.
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="builder-waste-handling">Quantities</label>
            <Select
              id="builder-waste-handling"
              value={builderForm.data.customer.waste_handling}
              onChange={(e) =>
                builderForm.setData("customer", {
                  ...builderForm.data.customer,
                  waste_handling: e.target.value as typeof customer.waste_handling,
                })
              }
              data-testid="builder-waste-handling"
            >
              <option value="net">Net SF — waste absorbed in the quoted price</option>
              <option value="waste_in">Waste in — SF inflated by each category&apos;s waste %</option>
            </Select>
          </div>
          <div className="modal-footer">
            <Button type="submit" variant="soft" disabled={builderForm.processing} data-testid="builder-save">
              Save builder
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
